import { ApiError } from "@/api/client";
import { createSavedSearch, deleteSavedSearch, listSavedSearches } from "@/api/searches";
import { countActiveFilters } from "@/lib/filters";
import { formatDateBR } from "@/lib/format";
import { UI } from "@/lib/labels";
import type { SavedSearch, SearchFilters } from "@/types/api";
import { Check, Copy, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Modal } from "./ui/modal";
import { Spinner } from "./ui/spinner";

interface SaveShareModalProps {
  open: boolean;
  onClose: () => void;
  currentFilters: SearchFilters;
  shareUrl: string;
  onApply: (filters: SearchFilters) => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Erro inesperado.";
}

/** Modal para salvar a pesquisa atual, aplicar/excluir salvas e copiar o link. */
export function SaveShareModal({
  open,
  onClose,
  currentFilters,
  shareUrl,
  onApply,
}: SaveShareModalProps) {
  const [name, setName] = useState("");
  const [items, setItems] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setSaveError(null);
      setCopied(false);
      return;
    }
    let active = true;
    setLoading(true);
    setListError(null);
    listSavedSearches()
      .then((list) => {
        if (active) {
          setItems(list);
          setLoading(false);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setListError(errorMessage(error));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [open]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setSaveError("Dê um nome à pesquisa.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const created = await createSavedSearch(trimmed, currentFilters);
      setName("");
      setItems((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
    } catch (error) {
      setSaveError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSavedSearch(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      setListError(errorMessage(error));
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Área de transferência indisponível — o link pode ser copiado manualmente.
    }
  };

  const applyAndClose = (filters: SearchFilters) => {
    onApply(filters);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={UI.actions.saveShare}>
      <div className="space-y-6">
        {/* Compartilhar por link */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Compartilhar por link</h3>
          <p className="text-xs text-muted-foreground">
            Qualquer vendedor logado que abrir este link verá a mesma pesquisa.
          </p>
          <div className="flex gap-2">
            <Input
              value={shareUrl}
              readOnly
              aria-label="Link da pesquisa"
              className="font-mono text-xs"
            />
            <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
              {copied ? (
                <Check className="h-4 w-4 text-primary" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? "Copiado!" : UI.actions.copyLink}
            </Button>
          </div>
        </section>

        {/* Salvar pesquisa */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Salvar pesquisa</h3>
          <p className="text-xs text-muted-foreground">
            {countActiveFilters(currentFilters)} filtro(s) ativo(s) serão salvos.
          </p>
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Nome da pesquisa"
              aria-label="Nome da pesquisa"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleSave();
                }
              }}
            />
            <Button type="button" onClick={handleSave} disabled={saving} className="shrink-0">
              {saving ? <Spinner className="h-4 w-4" /> : null}
              {UI.actions.save}
            </Button>
          </div>
          {saveError ? (
            <p className="text-xs text-destructive" role="alert">
              {saveError}
            </p>
          ) : null}
        </section>

        {/* Pesquisas salvas */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Pesquisas salvas</h3>
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" />
              Carregando…
            </div>
          ) : listError ? (
            <p className="text-sm text-destructive" role="alert">
              {listError}
            </p>
          ) : items.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">Nenhuma pesquisa salva ainda.</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {countActiveFilters(item.filters)} filtro(s) · {formatDateBR(item.created_at)}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyAndClose(item.filters)}
                    >
                      {UI.actions.apply}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item.id)}
                      aria-label={`Excluir ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
